import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";
import { cityPriceFaq, citySeo } from "./cityPricing";

const saintTropez: ChauffeurPage = {
  slug: "saint-tropez",
  title: "VTC & chauffeur privé à Saint-Tropez",
  subtitle:
    "Chauffeur privé à Saint-Tropez : plages de Pampelonne, port, villas et transfert depuis l'aéroport de Nice.",
  keywords: [
    "vtc saint-tropez",
    "chauffeur privé saint-tropez",
    "chauffeur pampelonne",
    "transfert aéroport nice saint-tropez",
  ],
  hero: {
    title: "VTC & chauffeur privé à Saint-Tropez",
    subtitle:
      "Pampelonne, le port et les villas du golfe : un chauffeur pour toute la saison, 24h/24.",
    keywords: [
      "VTC Saint-Tropez",
      "chauffeur Pampelonne",
      "chauffeur golfe de Saint-Tropez",
      "transfert Nice Saint-Tropez",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & chauffeur privé à Saint-Tropez",
    badges: [
      "Plages de Pampelonne",
      "Transfert longue distance",
      "Service 24/7",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Saint-Tropez",
    items: [
      {
        title: "Plages de Pampelonne",
        description:
          "Beach clubs et restaurants de plage : dépose et reprise sans souci de parking en saison.",
      },
      {
        title: "Transfert longue distance",
        description:
          "Depuis l'aéroport de Nice, près de 2 h de route gérées en confort haut de gamme.",
      },
      {
        title: "Clientèle villas & yachting",
        description:
          "Propriétaires, invités et équipages : discrétion et horaires souples tout l'été.",
      },
      {
        title: "Saison maîtrisée",
        description:
          "Trafic estival du golfe, accès au port et au village anticipés par le chauffeur.",
      },
    ],
  },
  services: {
    heading: "Services chauffeur à Saint-Tropez",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Liaison longue distance Nice ↔ Saint-Tropez, véhicule spacieux et suivi de vol.",
      },
      {
        title: "Navettes plages",
        text: "Vers les beach clubs de Pampelonne (Ramatuelle) et retour, sans contrainte de stationnement.",
      },
      {
        title: "Mise à disposition",
        text: "Chauffeur dédié à la journée pour villas, déjeuners et sorties dans le golfe.",
      },
      {
        title: "Soirées & port",
        text: "Restaurants du port, clubs et événements, retour de nuit en toute sécurité.",
      },
      {
        title: "Yachting & villas",
        text: "Transferts équipages et invités, coordination avec la conciergerie de villa.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Port de Saint-Tropez",
        description: "Quais, restaurants et yachts au cœur du village.",
      },
      {
        name: "Plage de Pampelonne",
        description: "Beach clubs de Ramatuelle, accès saisonnier.",
      },
      {
        name: "Place des Lices",
        description: "Marché, cafés et centre du village.",
      },
      {
        name: "La Ponche",
        description: "Vieux quartier de pêcheurs et ruelles.",
      },
      {
        name: "Ramatuelle & Gassin",
        description: "Villages perchés et villas du golfe.",
      },
      {
        name: "Port Grimaud",
        description: "Cité lacustre à l'entrée du golfe.",
      },
    ],
  },
  generic: {
    heading: "Saint-Tropez, accès saisonnier et longues distances",
    subheading:
      "L'aéroport de Nice est loin, le golfe se sature l'été : un chauffeur qui l'anticipe.",
    paragraphs: [
      "Saint-Tropez n'a pas d'aéroport : l'arrivée se fait le plus souvent depuis Nice Côte d'Azur, soit près de deux heures de route. Sur cette distance, un véhicule spacieux, l'eau, les chargeurs et un chauffeur reposé font toute la différence ; nous calons l'heure de départ pour limiter l'attente à l'entrée du golfe.",
      "En haute saison, la route du littoral et les abords du port se saturent. Pour rejoindre les beach clubs de Pampelonne ou dîner sur le port, la dépose en porte-à-porte évite la quête d'un stationnement devenu rare et cher.",
      "La clientèle villas et yachting attend souplesse et discrétion : horaires mouvants, allers-retours plage, coordination avec la conciergerie. La mise à disposition garde le même chauffeur disponible tout au long de la journée et de la soirée.",
    ],
    bullets: ["Véhicule spacieux", "Eau & chargeurs", "Horaires souples"],
  },
  process: {
    heading: "Réserver un chauffeur à Saint-Tropez",
    steps: [
      "Indiquez votre vol à Nice ou votre adresse dans le golfe (villa, hôtel, port).",
      "Nous recommandons le véhicule selon la distance, les bagages et le groupe.",
      "Le chauffeur suit le vol et cale le départ pour fluidifier l'arrivée.",
      "Transfert direct ou mise à disposition pour plages et sorties du séjour.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Saint-Tropez", "SAINT_TROPEZ"),
    {
      q: "Combien de temps pour aller de l'aéroport de Nice à Saint-Tropez ?",
      a: "Comptez environ 1h30 hors saison et jusqu'à 2h30 lors des week-ends d'été et des pics de trafic dans le golfe. Nous adaptons l'heure de départ en conséquence.",
    },
    {
      q: "Quel véhicule pour ce long transfert avec bagages ?",
      a: "Pour la distance et les bagages, un van premium (Class V) est le plus confortable. Les berlines Classe E/S conviennent pour 2 à 3 voyageurs voyageant léger.",
    },
    {
      q: "Desservez-vous les beach clubs de Pampelonne ?",
      a: "Oui, nous assurons les navettes vers les plages de Pampelonne (Ramatuelle) et le retour, sans contrainte de stationnement, avec horaires souples l'été.",
    },
    {
      q: "Le service est-il disponible toute la saison estivale ?",
      a: "Oui, et la réservation à l'avance est vivement conseillée de juin à septembre : la demande est forte sur le golfe de Saint-Tropez et les disponibilités se réduisent vite.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à la clientèle internationale des villas et du yachting du golfe.",
    },
    {
      q: "Intervenez-vous pendant Les Voiles de Saint-Tropez ?",
      a: "Oui. Pendant Les Voiles de Saint-Tropez et les pics d'affluence, nous anticipons les accès saturés au port et au village, et la mise à disposition assure vos allers-retours.",
    },
    {
      q: "Depuis quelles villes assurez-vous le transfert vers Saint-Tropez ?",
      a: "Depuis l'aéroport de Nice, Cannes (env. 1h20) et Monaco (env. 2h), mais aussi depuis toute la Côte d'Azur, en van premium pour le confort sur longue distance.",
    },
  ],
  relatedLinks: [
    {
      label: "Transfert Aéroport de Nice ⇄ Saint-Tropez",
      href: "/chauffeur/transferts/aeroport-nice-saint-tropez",
    },
    {
      label: "Transfert Cannes ⇄ Saint-Tropez",
      href: "/chauffeur/transferts/cannes-saint-tropez",
    },
    {
      label: "Van avec chauffeur (longue distance & bagages)",
      href: "/chauffeur/van-avec-chauffeur",
    },
    {
      label: "Tarifs de location à Saint-Tropez",
      href: "/location/location-voiture-saint-tropez",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Saint-Tropez",
    text: "Transfert depuis Nice, plage de Pampelonne ou soirée au port : réservez votre chauffeur.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver un chauffeur",
  },
  seo: {
    // MESURE 2026-10-09 : titre sans prix, page en position 36 à 45.
    ...citySeo("fr", "Saint-Tropez", "SAINT_TROPEZ", ", VTC"),
    canonical: "/chauffeur/saint-tropez",
  },
};

export default saintTropez;
